import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Pencil, Eye } from "lucide-react";
import MaterialArticleView from "@/components/MaterialArticleView";

const STATUS_LABEL = {
  draft: "Черновик",
  review: "На проверке",
  published: "Опубликовано",
  hidden: "Скрыто",
};

const STATUS_TONE = {
  draft: "bg-amber-100 text-amber-900 border-amber-200",
  review: "bg-blue-100 text-blue-900 border-blue-200",
  published: "bg-emerald-100 text-emerald-900 border-emerald-200",
  hidden: "bg-gray-200 text-gray-800 border-gray-300",
};

export default function AdminMaterialPreview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/materials/${id}`);
      setMaterial(data);
      setNotFound(false);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else if (e.response?.status === 403) toast.error("Требуются права администратора");
      else toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream" data-testid="admin-preview-loading">
        <div className="max-w-3xl mx-auto px-5 py-10">
          <div className="h-6 w-24 bg-sand/40 rounded animate-pulse" />
          <div className="h-10 w-3/4 bg-sand/40 rounded animate-pulse mt-4" />
          <div className="h-64 bg-sand/30 rounded-3xl animate-pulse mt-6" />
        </div>
      </div>
    );
  }

  if (notFound || !material) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5" data-testid="admin-preview-notfound">
        <p className="font-serif text-2xl text-espresso">Материал не найден</p>
        <button
          onClick={() => nav("/admin?tab=materials")}
          className="chip bg-espresso text-white mt-6 px-5 py-2"
          data-testid="admin-preview-back-notfound"
        >
          К материалам
        </button>
      </div>
    );
  }

  const tone = STATUS_TONE[material.status] || STATUS_TONE.draft;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-preview-page">
      {/* Preview ribbon — admin-only visual banner, never sent to real users */}
      <div className="bg-espresso text-cream" data-testid="admin-preview-ribbon">
        <div className="max-w-5xl mx-auto px-5 py-2 flex items-center gap-3 text-xs uppercase tracking-widest">
          <Eye className="w-4 h-4" />
          <span>Предпросмотр администратора</span>
          <span className={`ml-auto inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${tone}`}
                data-testid="admin-preview-status">
            {STATUS_LABEL[material.status] || material.status}
          </span>
          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium bg-white text-espresso border-white/50"
                data-testid="admin-preview-freeflag">
            {material.is_free ? "Бесплатно · Open Library" : "Платный / вне Open Library"}
          </span>
        </div>
      </div>

      <header className="max-w-3xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button
          onClick={() => nav("/admin?tab=materials")}
          data-testid="admin-preview-back-button"
          className="inline-flex items-center gap-1 -ml-1 pl-1 pr-3 py-1.5 rounded-full hover:bg-sand/50 text-espresso"
          aria-label="Назад в админ"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Назад в админ</span>
        </button>
        <button
          onClick={() => nav(`/admin?tab=materials&edit=${material.id}`)}
          data-testid="admin-preview-edit-button"
          className="ml-auto chip px-4 py-2 border bg-espresso text-white border-espresso hover:bg-espressoDark transition-colors flex items-center gap-2"
        >
          <Pencil className="w-4 h-4" />
          Редактировать
        </button>
      </header>

      <MaterialArticleView material={material} testidPrefix="admin-preview" />

      {/* Explicit visibility notice — makes it crystal-clear what the student will see */}
      <div className="max-w-3xl mx-auto px-5 pb-16 -mt-6">
        <div className="rounded-2xl border hairline bg-white/70 p-4 text-sm text-espresso" data-testid="admin-preview-visibility">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Видимость после публикации</div>
          {material.status === "published" && material.is_free ? (
            <p>Этот материал <strong>виден в Открытой библиотеке</strong> — доступен всем ученикам и анонимным посетителям.</p>
          ) : material.status === "published" && !material.is_free ? (
            <p>Материал опубликован как <strong>платный</strong>: доступен только ученикам с активным AccessGrant на связанный продукт. В Открытой библиотеке он <strong>не показывается</strong>.</p>
          ) : (
            <p>Материал в статусе «{STATUS_LABEL[material.status] || material.status}» — <strong>публично не доступен</strong>. Смените статус на «Опубликовано» и включите «Бесплатно», чтобы он появился в Открытой библиотеке.</p>
          )}
        </div>
      </div>
    </div>
  );
}
